<script setup>
import { useUser } from '@frontJs/composables/user';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DateRangePicker from '@js/components/pickers/DateRangePicker.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import NumbersCell from '@js/components/tables/cells/NumbersCell.vue';
import PercentagesCell from '@js/components/tables/cells/PercentagesCell.vue';
import PricesCell from '@js/components/tables/cells/PricesCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { hasRole } from '@js/utils/permission';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type, apikom } = defineProps({
    type: {
        type: String,
        required: false,
    },
    apikom: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const { currentUserEmail } = useUser();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                hasRole('super_admin')
                    ? h(LinkCell, {
                          text: props.row.original.name,
                          href: route(
                              'front.projectManagement.customer.updateCustomer',
                              {
                                  tenant: props.row.original.id,
                              },
                          ),
                      })
                    : props.row.original.url
                      ? h(LinkCell, {
                            text: props.row.original.name,
                            href: props.row.original.url,
                        })
                      : h(TextCell, {
                            text: props.row.original.name,
                        }),
        }),
        columnHelper.accessor(() => null, {
            id: 'turnoverOfToday',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA du jour',
                }),
            cell: (props) =>
                h(PricesCell, {
                    price1: props.row.original.turnoverOfToday,
                    withDecimals: false,
                }),
            footer: () =>
                h(PricesCell, {
                    price1: globalData.value.turnoverOfToday,
                    withDecimals: false,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'turnoverOfYesterday',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA hier',
                }),
            cell: (props) =>
                h(PricesCell, {
                    price1: props.row.original.turnoverOfYesterday,
                    withDecimals: false,
                }),
            footer: () =>
                h(PricesCell, {
                    price1: globalData.value.turnoverOfYesterday,
                    withDecimals: false,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'baseTurnover',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA',
                }),
            cell: (props) =>
                h(PricesCell, {
                    price1: props.row.original.baseTurnover,
                    price2: props.row.original.comparisonTurnover,
                    withDecimals: false,
                    color:
                        props.row.original.comparisonTurnover === undefined
                            ? null
                            : props.row.original.baseTurnover ===
                                props.row.original.comparisonTurnover
                              ? 'orange'
                              : props.row.original.baseTurnover >
                                  props.row.original.comparisonTurnover
                                ? 'green'
                                : 'red',
                }),
            footer: () =>
                h(PricesCell, {
                    price1: globalData.value.baseTurnover,
                    price2: globalData.value.comparisonTurnover,
                    withDecimals: false,
                    color:
                        globalData.value.comparisonTurnover === undefined
                            ? null
                            : globalData.value.baseTurnover ===
                                globalData.value.comparisonTurnover
                              ? 'orange'
                              : globalData.value.baseTurnover >
                                  globalData.value.comparisonTurnover
                                ? 'green'
                                : 'red',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'netMargin',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Marge<br/>nette HT',
                }),
            cell: (props) =>
                h(PricesCell, {
                    price1: props.row.original.baseNetMargin,
                    price2: props.row.original.comparisonNetMargin,
                    withDecimals: false,
                    color:
                        props.row.original.comparisonNetMargin === undefined
                            ? null
                            : props.row.original.baseNetMargin ===
                                props.row.original.comparisonNetMargin
                              ? 'orange'
                              : props.row.original.baseNetMargin >
                                  props.row.original.comparisonNetMargin
                                ? 'green'
                                : 'red',
                }),
            footer: () =>
                h(PricesCell, {
                    price1: globalData.value.baseNetMargin,
                    price2: globalData.value.comparisonNetMargin,
                    withDecimals: false,
                    color:
                        globalData.value.comparisonTurnover === undefined
                            ? null
                            : globalData.value.baseNetMargin ===
                                globalData.value.comparisonNetMargin
                              ? 'orange'
                              : globalData.value.baseNetMargin >
                                  globalData.value.comparisonNetMargin
                                ? 'green'
                                : 'red',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfProcessingOrders',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB cdes<br/>à traiter',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfProcessingOrders,
                }),
            footer: () =>
                h(NumberCell, {
                    number: globalData.value.numberOfProcessingOrders,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfProcessingOrders',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB tickets<br/>à traiter',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original
                        .numberOfToBeProcessedCustomerTickets,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseConversions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Conv. SEA',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(NumbersCell, {
                          number1: props.row.original.baseConversions,
                          number2: props.row.original.comparisonConversions,
                          color:
                              props.row.original.comparisonConversions ===
                              undefined
                                  ? null
                                  : props.row.original.baseConversions ===
                                      props.row.original.comparisonConversions
                                    ? 'orange'
                                    : props.row.original.baseConversions >
                                        props.row.original.comparisonConversions
                                      ? 'green'
                                      : 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSeaCost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût SEA',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(PricesCell, {
                          price1: props.row.original.baseSeaCost,
                          price2: props.row.original.comparisonSeaCost,
                          withDecimals: false,
                      })
                    : null,
            footer: () =>
                h(PricesCell, {
                    price1: globalData.value.baseSeaCost,
                    price2: globalData.value.comparisonSeaCost,
                    withDecimals: false,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'baseCostPerConversion',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût/Conv. SEA',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(PricesCell, {
                          price1: props.row.original.baseCostPerConversion,
                          price2: props.row.original
                              .comparisonCostPerConversion,
                          withDecimals: false,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSeaCostsToOrderTurnoverRatio',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEA % CA HT',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(PercentagesCell, {
                          percentage1:
                              props.row.original
                                  .baseSeaCostsToOrderTurnoverRatio,
                          percentage2:
                              props.row.original
                                  .comparisonSeaCostsToOrderTurnoverRatio,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSeaRoas',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS SEA',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(NumbersCell, {
                          number1: props.row.original.baseSeaRoas,
                          number2: props.row.original.comparisonSeaRoas,
                          withDecimals: true,
                          color:
                              props.row.original.comparisonSeaRoas === undefined
                                  ? null
                                  : props.row.original.baseSeaRoas ===
                                      props.row.original.comparisonSeaRoas
                                    ? 'orange'
                                    : props.row.original.baseSeaRoas >
                                        props.row.original.comparisonSeaRoas
                                      ? 'green'
                                      : 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSmaCost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût SMA',
                }),
            cell: (props) =>
                props.row.original.facebookAdsActive
                    ? h(PricesCell, {
                          price1: props.row.original.baseSmaCost,
                          price2: props.row.original.comparisonSmaCost,
                          withDecimals: false,
                      })
                    : null,
            footer: () =>
                h(PricesCell, {
                    price1: globalData.value.baseSmaCost,
                    price2: globalData.value.comparisonSmaCost,
                    withDecimals: false,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSmaCostsToOrderTurnoverRatio',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMA % CA HT',
                }),
            cell: (props) =>
                props.row.original.facebookAdsActive
                    ? h(PercentagesCell, {
                          percentage1:
                              props.row.original
                                  .baseSmaCostsToOrderTurnoverRatio,
                          percentage2:
                              props.row.original
                                  .comparisonSmaCostsToOrderTurnoverRatio,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSmaRoas',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS SMA',
                }),
            cell: (props) =>
                props.row.original.facebookAdsActive
                    ? h(NumbersCell, {
                          number1: props.row.original.baseSmaRoas,
                          number2: props.row.original.comparisonSmaRoas,
                          withDecimals: true,
                          color:
                              props.row.original.comparisonSmaRoas === undefined
                                  ? null
                                  : props.row.original.baseSmaRoas ===
                                      props.row.original.comparisonSmaRoas
                                    ? 'orange'
                                    : props.row.original.baseSmaRoas >
                                        props.row.original.comparisonSmaRoas
                                      ? 'green'
                                      : 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseAdCostsToOrderTurnoverRatio',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'PUB / CA HT %',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive ||
                props.row.original.facebookAdsActive
                    ? h(PercentagesCell, {
                          percentage1:
                              props.row.original
                                  .baseAdCostsToOrderTurnoverRatio,
                          suffix1: props.row.original.roasTargetPercentage
                              ? `(${props.row.original.roasTargetPercentage}%)`
                              : '',
                          percentage2:
                              props.row.original
                                  .comparisonAdCostsToOrderTurnoverRatio,
                          withDecimals: true,
                          color:
                              props.row.original
                                  .comparisonAdCostsToOrderTurnoverRatio ===
                              undefined
                                  ? null
                                  : props.row.original.roasTargetPercentage
                                    ? props.row.original
                                          .baseAdCostsToOrderTurnoverRatio ===
                                      props.row.original.roasTargetPercentage
                                        ? 'orange'
                                        : Math.trunc(
                                                props.row.original
                                                    .baseAdCostsToOrderTurnoverRatio,
                                            ) >
                                            props.row.original
                                                .roasTargetPercentage
                                          ? 'red'
                                          : 'green'
                                    : 'orange',
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) => {
                const original = props.row.original;

                const loginApikomUrl = `/${original.id}/login?token=${original.superAdminDatakeplerLoginToken}`;

                const loginCustomerUrl = original.superAdminDatakeplerLoginToken
                    ? `/${original.id}/login?token=${original.adminLoginToken}`
                    : original.loginToken
                      ? `/${original.id}/login?token=${original.loginToken}&asCustomer=1`
                      : `/${original.id}/login?token=${original.adminLoginToken}&asCustomer=1`;

                return h(
                    BaseCell,
                    { class: 'flex-col justify-center' },
                    {
                        default: () => [
                            original.superAdminDatakeplerLoginToken
                                ? h(
                                      ActionButton,
                                      {
                                          size: 'small',
                                          href: loginApikomUrl,
                                          target: '_blank',
                                          class: 'w-full whitespace-nowrap',
                                      },
                                      () => 'Connexion Apikom',
                                  )
                                : null,
                            h(
                                ActionButton,
                                {
                                    size: 'small',
                                    color: 'secondary',
                                    href: loginCustomerUrl,
                                    target: '_blank',
                                    class: 'w-full whitespace-nowrap',
                                    disabled:
                                        !original.loginToken &&
                                        !original.adminLoginToken,
                                },
                                () => 'Connexion client',
                            ),
                        ],
                    },
                );
            },
        }),
    ],
    url: route('front.postDashboard'),
    data: {
        type,
        apikom,
    },
    columnVisibility: {
        turnoverOfToday:
            type === 'e-commerce' ||
            type === 'marketplace' ||
            type === 'shop' ||
            type === undefined,
        turnoverOfYesterday:
            type === 'e-commerce' ||
            type === 'marketplace' ||
            type === 'shop' ||
            type === undefined,
        baseTurnover:
            type === 'e-commerce' ||
            type === 'marketplace' ||
            type === 'shop' ||
            type === undefined,
        numberOfProcessingOrders:
            apikom ||
            ((type === 'e-commerce' ||
                type === 'marketplace' ||
                type === undefined) &&
                (currentUserEmail === 'marketing@apikom.fr' ||
                    currentUserEmail === 'deborah@apikom.fr' ||
                    currentUserEmail === 'marion@apikom.fr' ||
                    currentUserEmail === 'pauline@apikom.fr')),
        numberOfCustomerTicketsToProcess:
            apikom ||
            ((type === 'e-commerce' ||
                type === 'marketplace' ||
                type === undefined) &&
                (currentUserEmail === 'marketing@apikom.fr' ||
                    currentUserEmail === 'deborah@apikom.fr' ||
                    currentUserEmail === 'marion@apikom.fr' ||
                    currentUserEmail === 'pauline@apikom.fr')),
        netMargin:
            apikom ||
            ((type === 'e-commerce' ||
                type === 'marketplace' ||
                type === undefined) &&
                (currentUserEmail === 'marketing@apikom.fr' ||
                    currentUserEmail === 'deborah@apikom.fr' ||
                    currentUserEmail === 'marion@apikom.fr' ||
                    currentUserEmail === 'pauline@apikom.fr')),
        baseConversions: type === 'showcase',
        baseSeaCost:
            type === 'e-commerce' || type === 'showcase' || type === undefined,
        baseCostPerConversion: type === 'showcase',
        baseSeaCostsToOrderTurnoverRatio:
            type === 'e-commerce' || type === undefined,
        baseSeaRoas:
            type === 'e-commerce' || type === 'showcase' || type === undefined,
        baseSmaCost:
            type === 'e-commerce' ||
            type === 'showcase' ||
            type === 'shop' ||
            type === undefined,
        baseSmaCostsToOrderTurnoverRatio:
            type === 'e-commerce' || type === 'shop' || type === undefined,
        baseSmaRoas:
            type === 'e-commerce' ||
            type === 'shop' ||
            type === 'showcase' ||
            type === undefined,
        baseAdCostsToOrderTurnoverRatio:
            type === 'e-commerce' || type === 'shop' || type === undefined,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
        with-row-number
        with-footer
    >
        <template #actions>
            <DateRangePicker :callback="updateData" compare />
        </template>
    </DataTableLayout>
</template>
